/**
 * Reads the cover period off the text layer of ONE document class — the statutory CERTIFICATE OF
 * INSURANCE issued under the Motor Vehicles (Third Party Insurance) Act, 1958 — so the setup
 * surfaces can prefill two dates the admin currently retypes by eye.
 *
 * PURE MODULE: no React, no I/O, and deliberately NO import of `@/lib/sbt-pdf` — that module's
 * `?url` worker specifier is Vite-only and vitest's node environment cannot resolve it, not even as
 * a type import. Input is text already extracted by `extractPdfText` (every text run joined with
 * '\n', pages joined the same way); output is 'yyyy-MM-dd' strings assembled from the matched
 * digits, never a Date.
 *
 * WHAT THIS PARSER READS, AND WHY IT IS ONLY THAT
 *
 * Six adversarial passes were run over a parser that read any labelled cover period — 'Cover
 * Starts', 'Period of Insurance', 'Valid From/To', 'Start Date', 'Expiry Date' and their synonyms —
 * off schedules, cover notes and renewal notices as well as certificates. Every pass found fresh,
 * realistic layouts that produced a complete, plausible, SILENTLY WRONG period, and the dangerous
 * ones were all the same shape: a date exactly one year from the right answer, which no span check,
 * no plausibility window and no downstream one-year warning can catch. Six rounds of defences
 * (heading runs, backwards qualifier lookback, statutory-only mode, header-row capping) closed each
 * batch and each round refused more real layouts than the last.
 *
 * THE RULING: read ONLY the statutory certificate, whose period is fixed BY LAW in fields 3 and 4,
 * and nothing else. Precision over recall. A miss costs the admin the five seconds they already
 * spend reading the two dates off the document on screen; a wrong prefill converts a random human
 * typo into a systematic, machine-looking value the admin is LESS likely to re-check precisely
 * because it looks derived. Every generic label is gone from anchoring — not narrowed, gone — and
 * with it every defence that existed only to keep generic labels honest.
 *
 * THE GATE. All four conditions are evaluated over the WHOLE document, and any failure is
 * `none` / 'not-a-statutory-certificate':
 *
 *  G1. THE ACT TITLE IS PRINTED. `ACT_TITLE` below, tolerating the '1958' and '(GHANA)' tails and
 *      the line break SIC draws the title across (runs are joined with a space before matching).
 *      Every Ghanaian insurer prints it, because the Act requires it; nothing else does.
 *  G2. BOTH STATUTORY LABELS ARE PRESENT, AND THEY AGREE. Field 3 ('Effective Date of the
 *      Commencement of Insurance') and field 4 ('Date of Expiry of Insurance'). A page-2 repeat of
 *      the whole certificate is still ONE certificate, so the count that matters is of DISTINCT
 *      RESOLVED VALUES rather than of label occurrences: identical repeats collapse to one period
 *      and are a free cross-page consistency check, while two different values for one role are
 *      `ambiguous` / 'disagreeing-repeats' exactly as they always were.
 *  G3. NO COMPETING VOCABULARY ANYWHERE. `COMPETING_VOCABULARY` below, as whole words, document
 *      wide — no narrowing by position, by run shape or by distance from a label. Every one of the
 *      six passes beat a positional test by moving the offending word: a heading three rows up with
 *      a stamp in between, a qualifier on the far side of the value, a block printed before the
 *      labels rather than above them. Position is not a defence; presence is. The list is stated
 *      against the SIC template's OWN prose, which prints 'permitted', 'permission', 'licensing'
 *      and 'hire-purchase' in the statutory wording of fields 5 and 7 and 'Renewal' as a
 *      'Certificate Type:' value — so 'permit', 'licence no' and 'driving licence' are whole-word
 *      anchored, and BARE 'renewal' IS DELIBERATELY ABSENT. A renewal certificate is a certificate:
 *      'Certificate Type: Renewal' and a 'RENEWAL CERTIFICATE' title both read. What IS listed is
 *      the vocabulary only a notice, an invoice or a blank uses — 'renewal notice', 'notice of
 *      renewal', 'renewal advice/invoice/statement/form/particulars/period/due', 'due for renewal',
 *      'we invite you to renew', 'renewing', 'renewed', 'lapse', 'grace period', 'continuation
 *      notice', 'debit note', 'specimen', 'sample certificate', 'claim form', 'date of accident' —
 *      every one of them a word a certificate has no reason to print and a notice can hardly avoid.
 *  G4. THE TWO VALUES COME ONLY FROM THE TWO STATUTORY LABELS, through the machinery below: the
 *      per-run sheet, caption-shaped runs terminating a window, noise labels poisoning what they
 *      can reach, a role that was claimed and refused staying closed, the ordering witness hearing
 *      only tokens a statutory label claimed, a value row of two dates being `contested` rather
 *      than guessed at, the year window, and the backwards / implausible-span checks.
 *
 * WHAT THE GATE COST, STATED RATHER THAN HIDDEN. A statutory certificate that cross-references the
 * policy it replaces — 'Previous policy number: MTC/2025/1' printed in its header — now refuses,
 * and that is pinned as a deliberate false negative in the suite. So does a certificate printed
 * beside a road-tax sticker, a brown card or an endorsement. All of them are documents whose period
 * this parser could probably read; none of them is worth the class of silent wrong read that the
 * same tolerance let in six times running.
 *
 * STATED RESIDUALS — WHAT STILL GETS THROUGH, NAMED RATHER THAN IMPLIED. G3 is a vocabulary, and a
 * vocabulary is a list of words somebody else chose to print. Three cases survive it by
 * construction, and none of them is a bug to be fixed here:
 *
 *  (a) A RENEWAL LETTER THAT REPRINTS FIELDS 3 AND 4 IN WORDS THIS LIST HAS NEVER HEARD. An
 *      insurer's covering letter can quote the Act title, quote both statutory labels, and ask for
 *      the money in prose containing not one listed word ('Kindly call at any of our offices to
 *      continue your cover.'). It passes all four conditions and reads LAST YEAR's period —
 *      complete, one clean year, internally consistent. Pinned as it behaves in the suite, not as
 *      anyone would like it to behave.
 *  (b) LAST YEAR'S GENUINE CERTIFICATE. There is nothing wrong with it at all: it is a statutory
 *      certificate, this parser reads it correctly, and the only thing wrong is that the admin
 *      uploaded the wrong year's paper. No document-side test can exist for this.
 *
 *      (a) AND (b) ARE THE SAME FAILURE ON SCREEN, AND THEY SURFACE THE SAME WAY. Both hand back a
 *      one-year period, so `policyPeriodWarning` — which only ever questions the SPAN — is silent
 *      on both. What separates them from a live certificate is not on the page: it is today. So
 *      `prefillLine` trails every read sentence with 'This cover ended on …' whenever the read's
 *      expiry is before the business day, and that sentence is the ONLY signal for this class.
 *  (c) A STRAY RUN ANSWERING A STATUTORY FIELD. The caption rule keeps exactly two shapes
 *      transparent, because they are the shapes a real value is printed in: a bare value run and a
 *      connector-prefixed one ('From 18/04/2026', '00:00hrs 17/04/2026'). A stray run of either
 *      shape drawn between a statutory label and its value is therefore read as that field's
 *      answer. Realistically only ONE end is captured that way — a stamp, a hand-written insert or
 *      a bracketed aside is not printed symmetrically into both fields — which leaves a MISMATCHED
 *      pair rather than a shifted one, and a mismatched pair is not a year: `policyPeriodWarning`
 *      says 'Cover runs N days, not a year' under the fields. The symmetric case, where the same
 *      stray shape lands in both fields and preserves the span, has no defence here either.
 *
 * HOW TO WIDEN IT, AND THE ONLY TWO WAYS WORTH TRYING. (1) The production breadcrumb: every read
 * leaves `{ status, reason }` behind (see `certificateRead.ts`), so the share of real uploads
 * refusing on 'not-a-statutory-certificate' — rather than on 'partial' or 'no-dates-in-reach' — is
 * measurable without ever seeing a customer's document. (2) The skip-if-absent real-sample fixture
 * at the foot of the suite: drop one real certificate's extracted text into
 * `tasks/insurance-certificates/` and the whole policy is finally tested against a document nobody
 * here wrote. Widen on THAT evidence. Do not widen on a fixture written by the same hand as the
 * regexes — the six passes are what that is worth.
 *
 * IT ALSO READS WHICH VEHICLE THE CERTIFICATE NAMES, AND ONLY BEHIND THE SAME GATE. A `found` read
 * carries `vehicle` — statutory field 1's registration number and the chassis number printed below
 * field 8 — read with this module's own machinery (labels reach forward, caption-shaped runs
 * terminate, every label ends every other label's window) and handed back RAW. It is a second
 * answer to the same question the gate already asked, not a second entry point: §6 states why the
 * coupling is the point rather than a cost, and `certificateVehicle` owns what the two identifiers
 * are then compared against. A vehicle field that is missing or shaped oddly is an absent key and
 * never a failed date read.
 *
 * THE RULES THAT SURVIVED THE RULING, AND WHY EACH IS STILL HERE
 *
 * TEXT RUNS ARE BOUNDARIES, NOT WHITESPACE. The extractor emits one run per drawn string and this
 * module keeps every boundary (`Sheet.runs`) alongside the collapsed string. A run is CAPTION-SHAPED
 * when it carries a ':', opens with a date-bearing word, introduces its first date with anything but
 * a range connector, or prints anything but further dates and range punctuation AFTER that date —
 * and a caption-shaped run ends every open label window, whether or not the caption is a phrase this
 * file has ever heard of. That is the rule no vocabulary could give: an unknown caption used to be
 * invisible, so 'Effective Date of the Commencement of Insurance:' reached straight over 'Date of
 * Issuance: 10/04/2026' and prefilled the ISSUE date as the inception. Judging only the PREFIX was
 * half a rule — '10/04/2026 Page 1 of 2' and the SIC footer drawn beside its issue stamp both
 * passed as bare values — so the tail is judged too, and a numbered date ('2. 10/04/2026') is an
 * item in somebody's list rather than a value. Only a bare value run and a connector run ('From
 * 18/04/2026', '00:00hrs 17/04/2026') stay transparent, because those are the shapes a statutory
 * field's value is actually printed in. The stated cost: '18/04/2026 (see note)' is a real value
 * this parser refuses.
 *
 * DAY-FIRST IS NOT A DEFAULT. A numeric token is read only when it is self-determining (a component
 * over 12) or when another token THE STATUTORY LABELS CLAIMED witnesses the ordering. Otherwise the
 * read is `ambiguous`. Ghana writes day-first, but the NIC portal renders the same data through
 * `en-US`, and BOTH readings of 01/02/2026 -> 01/02/2027 are valid one-year periods, so nothing
 * downstream could catch a month-off guess. The witness is computed LAST, from the claims that
 * survived the year window, because a print stamp, a receipt line, an agent's date and a date of
 * birth are rendered by a different code path from the statutory fields and witness nothing about
 * them — one such token in en-US used to flip a whole Ghanaian certificate.
 *
 * A NOISE LABEL POISONS, AND IT POISONS THROUGH A ZERO-WIDTH WINDOW. Every token an issue stamp, a
 * date of birth or a payment date can reach is a token the statutory labels may not use. When the
 * noise label's own window is truncated to nothing by whatever follows it, it poisons the next token
 * in document order instead — which is what a label-block/value-block layout collapses to, and the
 * correct failure there is `contested`: the parser cannot tell which column a value belongs to, and
 * neither can a regex.
 *
 * A ROLE THAT WAS CLAIMED AND REFUSED IS CLOSED, NOT REOFFERED. The caption rule refuses a real
 * value on purpose ('18/04/2026 (see note)'), and that refusal must not leak into a repeat of the
 * same field printed further down the document.
 *
 * TWO-DIGIT YEARS AND SEPARATOR-LESS 8-DIGIT RUNS ARE REJECTED OUTRIGHT: no Ghanaian source prints
 * either on a certificate, and each opens a fresh ambiguity axis for an unmeasured gain. A labelled
 * period that is not one year still reads `found` — six-month cover is real, the UI warns about the
 * span (`policyPeriodWarning`, which runs off the DRAFT so it also warns on hand-typed dates), and
 * there is one definition of "a year" in the codebase rather than two. A cover date lies in
 * [now - 5 years, now + 3 years] or it is not this contract's cover period: asymmetric, because a
 * certificate is sometimes filed years late while nothing on a motor certificate is legitimately
 * three years out.
 */

import { differenceInCalendarDays } from 'date-fns';
import { parseBusinessDay } from '@/lib/format';
import type { CertificateVehicle } from '@/lib/certificateVehicle';

// ─── Public shape ────────────────────────────────────────────────────────────

export type CoverPeriodStatus = 'found' | 'ambiguous' | 'none';

export type CoverPeriodReason =
    | 'no-text' // nothing to read (empty/whitespace text layer — a scanned certificate)
    | 'not-a-statutory-certificate' // the gate refused: see G1–G3 in the docblock
    | 'no-dates-in-reach' // the statutory labels matched, but neither could reach a date it may use
    | 'partial' // one end anchored, the other not
    | 'contested' // a token claimed by both a statutory label and a noise label, or a value row of two
    | 'ordering-unknown' // a claimed numeric token has two readings and no witness forces one
    | 'disagreeing-repeats' // the same role resolved to two different dates
    | 'backwards' // expiry <= start
    | 'implausible-span'; // span > 1096 days

export interface CoverPeriodRead {
    status: CoverPeriodStatus;
    startsOn?: string; // 'yyyy-MM-dd' — present iff status === 'found'
    expiresOn?: string; // 'yyyy-MM-dd' — present iff status === 'found'
    raw?: { start: string; end: string }; // exactly as printed, e.g. '17/04/2026'
    labels?: { start: string; end: string }; // the matched label text, verbatim from the document
    reason?: CoverPeriodReason; // present iff status !== 'found'
    /**
     * WHICH VEHICLE THE CERTIFICATE NAMES, RAW AND ONLY EVER ON A `found` READ. Absent when
     * neither identifier could be read; either key absent when that one could not. See §7.
     */
    vehicle?: CertificateVehicle;
}

/** Forward reach of a statutory label, in characters of the normalised string. */
const WINDOW_CHARS = 120;
/** A mis-pairing ceiling, not a business rule: a 20-year "period" is a parse error. */
const MAX_SPAN_DAYS = 1096;
/*
 * THE PLAUSIBLE WINDOW IS ASYMMETRIC, BECAUSE THE TWO DIRECTIONS ARE NOT THE SAME RISK. A cover
 * date must fall in [now - 5 years, now + 3 years]. Backwards: a certificate is sometimes filed
 * long after it was issued, and a contract is sometimes set up against a year-old document, so a
 * few years of slack is real — but a 1996 period is never a prefill for a 2026 contract.
 * Forwards: nothing on a motor certificate is legitimately more than three years out (the longest
 * cover this parser will emit is MAX_SPAN_DAYS, three years), so a date beyond that is a typo.
 */
const YEARS_BACK = 5;
const YEARS_AHEAD = 3;

// ─── 1. Normalisation ────────────────────────────────────────────────────────

/** One extracted text run, addressed by its offsets in the collapsed string. */
interface Run {
    start: number;
    end: number;
    text: string;
    /**
     * Caption-shaped: it carries a ':', opens with a date-bearing word, introduces its first date
     * with something other than a range connector, or prints something else after it. See
     * `isCaption`.
     */
    caption: boolean;
}

/** The collapsed string every offset in this module refers to, plus the run boundaries in it. */
interface Sheet {
    s: string;
    runs: Run[];
}

interface Span {
    start: number;
    end: number;
}

/*
 * A HYPHEN-JOINED RANGE IS TWO DATES, NOT A LONGER NUMBER. '18/04/2026-17/04/2027' and
 * '2026-04-18-2027-04-17' both used to read as nothing at all: the boundary guard (correctly)
 * refuses a date with a digit-or-separator glued to its edge, so neither half survived and a
 * perfectly explicit statutory field came back empty. Split only where the shape is unambiguous —
 * a WHOLE `d/m/yyyy` date on each side of the '-', or one ISO date on each side.
 *
 * BOTH SIDES, AND THE LEFT ONE BOUNDARY-CLEAN. Matching the year alone on the left ('(\d{4})-')
 * manufactured a date out of an identifier: 'INV8841-12/04/2026' became 'INV8841 - 12/04/2026', and
 * an invoice number the boundary guard had correctly refused walked into a field as a cover date.
 * The left date must therefore be whole AND start on a non-date character, which is why the
 * preceding character is CAPTURED and put back rather than looked behind: a lookbehind literal is a
 * parse-time SyntaxError on Safari below 16.4, which would kill this lazy chunk rather than degrade
 * one read. Consuming that character can only cost a second split inside a chain of three glued
 * dates, a shape no certificate prints.
 */
const NUM_RANGE_JOIN = /(^|[^\d/.-])(\d{1,2}[/.]\d{1,2}[/.]\d{4})-(?=\d{1,2}[/.]\d{1,2}[/.]\d{4}\b)/g;
const ISO_RANGE_JOIN = /(\d{4}-\d{2}-\d{2})-(?=\d{4}-\d{2}-\d{2})/g;

/**
 * One run, whitespace-collapsed, because the statutory label wraps mid-phrase and arrives as two
 * or three separate text runs ('3. Effective Date of the Commencement of' / 'Insurance for the
 * Purpose of the Act:'). Collapsing is also what lets a label in column one sit 40 characters from
 * a value belonging to column two — the residual wrong-answer channel, narrowed by the window cap,
 * the next-label truncation, the caption terminator and the noise poison below, but never closed.
 */
function normaliseRun(raw: string): string {
    return (
        raw
            .normalize('NFKC') // NBSP -> space, fullwidth digits -> ascii
            .replace(/\u00AD/g, '') // soft hyphen (boeParser hit this on a real Ghana PDF)
            // EN and EM dash are RANGE separators ("1st Jan, 2025 – 31st Dec, 2025"); padded so the
            // boundary guard still sees a clean edge on each date rather than a '-' beside it.
            .replace(/[\u2013\u2014]/g, ' - ')
            // Hyphen, non-breaking hyphen, figure dash, horizontal bar and minus are INTRA-date
            // separators ("17\u2011Apr\u20112026").
            .replace(/[\u2010\u2011\u2012\u2015\u2212]/g, '-')
            .replace(ISO_RANGE_JOIN, '$1 - ')
            .replace(NUM_RANGE_JOIN, '$1$2 - ')
            .replace(/[\u2018\u2019\u201B]/g, "'") // curly quotes, so "proposer's signature" matches
            .replace(/\s+/g, ' ')
            .trim()
    );
}

/**
 * Times are never dates, and their digits must not become date fragments. Blanked before tokenising
 * and before captions are judged, so '00:00hrs 23/12/2025' and '2026-04-17T00:00:00Z' each leave
 * exactly one date and a value run carrying a time is not mistaken for a caption carrying a colon.
 *
 * LENGTH-PRESERVING ON PURPOSE: every offset in this module — runs, labels, tokens — indexes the
 * same string, so a replacement that changed the length would silently slide the run map.
 *
 * THE INSTANT FORM IS TAKEN FIRST AND WHOLE. '\b' never fires between the 'T' and the hour, so the
 * clock pattern alone leaves 'T00:' standing — one stray colon, which is all it takes for a value
 * run to be judged a caption and refuse to hand its date to the label above it.
 */
const ISO_TIME = /T\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?/g;
const TIME_OF_DAY = /\b\d{1,2}:\d{2}(?::\d{2})?(?:\.\d+)?\s*(?:hrs?|h|am|pm|a\.m\.|p\.m\.)?/gi;
const CLOCK_WORD = /\b(?:midnight|noon)\b/gi;

const blank = (m: string) => ' '.repeat(m.length);

function stripTimes(s: string): string {
    let text = s;
    // Order matters (the instant form first, see above), and every replacement is length-preserving,
    // so `exec` may keep walking the string it is rewriting.
    for (const re of [ISO_TIME, TIME_OF_DAY, CLOCK_WORD]) {
        re.lastIndex = 0;
        let m: RegExpExecArray | null;
        while ((m = re.exec(text)) !== null) {
            if (m[0].length === 0) {
                re.lastIndex += 1;
                continue;
            }
            const from = m.index;
            const to = from + m[0].length;
            text = text.slice(0, from) + blank(m[0]) + text.slice(to);
        }
    }
    return text;
}

/*
 * CAPTION-SHAPED, THE DENYLIST THAT NEEDS NO VOCABULARY. A run is a caption when any of these is
 * true: it carries a ':' (a field name, with or without its value printed after it); it OPENS with
 * a date-bearing word — 'Date Printed', 'Sticker Expiry 09/04/2027', 'Printed on 04/17/2026'; its
 * first date is INTRODUCED BY WORDS THAT ARE NOT A RANGE CONNECTOR — 'Ref 25/04/2026', 'Stamp
 * 12/04/2026', 'Signed at Accra 04/10/2026', 'INV8841-12/04/2026'; or anything but further dates
 * and range punctuation FOLLOWS its first date.
 *
 * THE OPEN-ENDED CLAUSES ARE THE ONES NO VOCABULARY COULD GIVE. A run whose caption word is not in
 * CAPTION_KEYWORD used to be transparent, so any stray dated line printed between a statutory label
 * and its value — a reference, a stamp, a signature block, an instalment — handed its own date to
 * the field above it. The word list can never be finished; "something introduces this date, and it
 * is not a connector" can.
 *
 * A RUN IS JUDGED BY BOTH SIDES OF ITS FIRST DATE. Reading only the prefix left the whole right-hand
 * half of a line invisible, and a certificate's furniture is printed exactly there: '10/04/2026 SIC
 * INSURANCE COMPANY LIMITED (www.sic-gh.com)' is the issue stamp drawn on the same line as the
 * footer, '10/04/2026 Page 1 of 2' is a page rule and '2026-04-10T09:15:00Z Server' is a server
 * stamp — every one of them a bare value run by the old test, and every one of them handed its date
 * to the label above. A value run is therefore transparent only when everything after its first
 * date is MORE dates, range connectors, punctuation, or the whitespace `stripTimes` left behind.
 *
 * A NUMBERING PREFIX IS A CAPTION, NOT A VALUE. '2. 10/04/2026' and '(1) 10/04/2026' used to be
 * transparent on the grounds that the statutory fields are numbered — but a numbered date is an
 * ITEM in somebody's list ('2. Date of the accident'), and the statutory fields carry their number
 * on the LABEL run ('4. Date of Expiry of Insurance:'), never on the value run.
 *
 * 'From' and 'To' are deliberately NOT captions — nor are 'until', 'till', 'through', 'and',
 * 'upto' and 'w.e.f.'. They are range connectors, and a statutory value is often printed as
 * 'From 18/04/2026'. A bare value run ('18/04/2026') and a run whose time has just been blanked
 * ('00:00hrs 17/04/2026') stay transparent for the same reason.
 */
const CAPTION_KEYWORD =
    /^(?:\d{1,2}\s*[.)]\s*)?(?:d\.?o\.?b|date\w*|issu\w*|print\w*|prepar\w*|receipt|receiv\w*|pay\w*|paid|propos\w*|endors\w*|sticker|renew\w*|birth\w*|expir\w*|effective|valid\w*|period\w*|commenc\w*|inception|start\w*|end\w*|cover\w*|policy|certificate|premium|schedule|generat\w*|creat\w*|updat\w*|registration|due)\b/i;

/**
 * The first date-SHAPED thing in a run, which is looser than the tokeniser on purpose: it has to
 * find the identifier fragments the tokeniser rightly refuses ('INV8841-12/04/2026') so that what
 * introduces them can be judged.
 */
const DATE_SHAPE =
    /\d{1,2}[/.-]\d{1,2}[/.-]\d{4}|\d{4}[/-]\d{1,2}[/-]\d{1,2}|\d{1,2}(?:st|nd|rd|th)?[- ]?(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)|(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?[- ]{1,2}\d{1,2}/i;

/** Everything a bare value may carry in front of it: punctuation and a range connector. */
const VALUE_PREFIX =
    /^[\s.,;)([\]*#-]*(?:(?:from|to|until|till|through|and|upto|up\s+to|w\.?e\.?f\.?)\b[\s.,;-]*)*$/i;

/**
 * Date-shaped AND greedy about the year, unlike `DATE_SHAPE`: this one is subtracted from the tail
 * of a run to find out what ELSE is printed there, so leaving ', 2026' behind would make every
 * month-name value run look like a caption.
 */
const TAIL_DATE =
    /\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4}|\d{4}[/-]\d{1,2}[/-]\d{1,2}|\d{1,2}(?:st|nd|rd|th)?[\s,-]{0,3}(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?[\s,-]{0,3}\d{0,4}|(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?[\s,-]{0,3}\d{1,2}(?:st|nd|rd|th)?,?[\s,-]{0,3}\d{0,4}/gi;

/** What may remain after the dates are subtracted from a value run's tail: punctuation and connectors. */
const VALUE_TAIL =
    /^[\s.,;)([\]*#/-]*(?:(?:from|to|until|till|through|and|upto|up\s+to|w\.?e\.?f\.?)\b[\s.,;)([\]*#/-]*)*$/i;

function isCaption(text: string): boolean {
    if (text.includes(':') || CAPTION_KEYWORD.test(text)) return true;
    const firstDate = DATE_SHAPE.exec(text);
    if (!firstDate) return false;
    if (!VALUE_PREFIX.test(text.slice(0, firstDate.index))) return true;
    TAIL_DATE.lastIndex = 0;
    return !VALUE_TAIL.test(text.slice(firstDate.index).replace(TAIL_DATE, ' '));
}

/** The collapsed string and its run map, built in one pass so the two can never disagree. */
function buildSheet(text: string): Sheet {
    const pieces: Span[] = [];
    let s = '';
    for (const raw of text.split('\n')) {
        const run = normaliseRun(raw);
        if (run === '') continue;
        if (s !== '') s += ' ';
        pieces.push({ start: s.length, end: s.length + run.length });
        s += run;
    }
    const stripped = stripTimes(s);
    const runs = pieces.map(p => {
        const runText = stripped.slice(p.start, p.end);
        return { start: p.start, end: p.end, text: runText, caption: isCaption(runText) };
    });
    return { s: stripped, runs };
}

/** Index of the last run starting at or before `pos`, or -1 when `pos` precedes every run. */
function runIndexAt(runs: Run[], pos: number): number {
    let lo = 0;
    let hi = runs.length - 1;
    let best = -1;
    while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (runs[mid].start <= pos) {
            best = mid;
            lo = mid + 1;
        } else {
            hi = mid - 1;
        }
    }
    return best;
}

// ─── 2. Date tokeniser ───────────────────────────────────────────────────────

interface DateToken {
    start: number;
    end: number;
    raw: string;
    /** De-duplicated admissible 'yyyy-MM-dd' readings: one for every pattern but NUM. */
    readings: string[];
    dayFirst?: string; // NUM only: the a=day, b=month reading
    monthFirst?: string; // NUM only: the a=month, b=day reading
    num?: { a: number; b: number }; // NUM only: the two leading components, for the witness vote
}

const ISO = /(\d{4})([/-])(\d{1,2})\2(\d{1,2})/g;
const DMY_TEXT = /(\d{1,2})(?:st|nd|rd|th)?[- ]?\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?[-, ]{0,2}(\d{4})/gi;
const MDY_TEXT = /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?[- ]{1,2}(\d{1,2})(?:st|nd|rd|th)?,?[- ]{1,2}(\d{4})/gi;
const NUM = /(\d{1,2})([/.-])(\d{1,2})\2(\d{4})/g;

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

/** A character that means the match is a fragment of something longer, not a date on its own. */
const EDGE_CHAR = /[0-9/.-]/;

/** 'yyyy-MM-dd' from calendar parts, or null when the parts are not a real day (31 Feb). */
function toYmd(y: number, m: number, d: number): string | null {
    if (m < 1 || m > 12 || d < 1 || d > 31) return null;
    // Built and read from LOCAL parts and never leaving the module as a Date, so this round trip
    // is deterministic under any host timezone.
    const t = new Date(y, m - 1, d);
    if (t.getFullYear() !== y || t.getMonth() !== m - 1 || t.getDate() !== d) return null;
    return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function monthNumber(name: string): number {
    return MONTHS.indexOf(name.slice(0, 3).toLowerCase()) + 1;
}

function tokenise(s: string): DateToken[] {
    const tokens: DateToken[] = [];
    const occupied: Span[] = [];

    const collect = (re: RegExp, build: (m: RegExpExecArray) => DateToken | null) => {
        re.lastIndex = 0;
        let m: RegExpExecArray | null;
        while ((m = re.exec(s)) !== null) {
            const from = m.index;
            const to = from + m[0].length;
            /*
             * BOUNDARY GUARD, WRITTEN BY HAND ON PURPOSE. A lookbehind REGEX LITERAL throws a
             * SyntaxError at parse time on Safari below 16.4, which would kill this whole lazy
             * chunk rather than degrade one read — and it would pass every local test and every CI
             * run, because vitest is node and CI builds are node too. The prefix-capture form
             * ('(^|[^0-9/.-])...') is no good either: it consumes the preceding character and then
             * silently misses a second date starting one character later. This is what rejects
             * '17/04/2026' inside 'MTC/P/17/04/2026'.
             */
            const before = from > 0 ? s[from - 1] : '';
            const after = s[to] ?? '';
            if (EDGE_CHAR.test(before) || EDGE_CHAR.test(after)) continue;
            if (occupied.some(span => from < span.end && to > span.start)) continue;
            const token = build(m);
            if (!token) continue;
            occupied.push({ start: from, end: to });
            tokens.push(token);
        }
    };

    const plain = (m: RegExpExecArray, iso: string | null): DateToken | null =>
        iso ? { start: m.index, end: m.index + m[0].length, raw: m[0], readings: [iso] } : null;

    collect(ISO, m => plain(m, toYmd(Number(m[1]), Number(m[3]), Number(m[4]))));
    collect(DMY_TEXT, m => plain(m, toYmd(Number(m[3]), monthNumber(m[2]), Number(m[1]))));
    collect(MDY_TEXT, m => plain(m, toYmd(Number(m[3]), monthNumber(m[1]), Number(m[2]))));
    collect(NUM, m => {
        const a = Number(m[1]);
        const b = Number(m[3]);
        const year = Number(m[4]);
        const dayFirst = toYmd(year, b, a);
        const monthFirst = toYmd(year, a, b);
        const readings = [...new Set([dayFirst, monthFirst].filter((v): v is string => v !== null))];
        if (readings.length === 0) return null;
        return {
            start: m.index,
            end: m.index + m[0].length,
            raw: m[0],
            readings,
            dayFirst: dayFirst ?? undefined,
            monthFirst: monthFirst ?? undefined,
            num: { a, b },
        };
    });

    return tokens.sort((x, y) => x.start - y.start);
}

// ─── 3. Ordering witness ─────────────────────────────────────────────────────

type Witness = 'day-first' | 'month-first' | 'none' | 'conflicting';

/**
 * Document-wide, computed once, over every syntactically valid numeric token A STATUTORY LABEL
 * CLAIMED and the year window KEPT. Nothing else is evidence: a token a noise label claimed, any
 * token no statutory label reached at all, and any token whose year says it is not this cover
 * period are print stamps, receipt lines, agents' dates, birth dates and typos rendered by a
 * different code path from fields 3 and 4, and one of them in en-US used to flip the whole
 * document. There is no fallback either — Ghana being day-first is not evidence about THIS
 * document.
 */
function witnessOf(tokens: DateToken[], canVote: (token: DateToken) => boolean): Witness {
    let day = false;
    let month = false;
    for (const token of tokens) {
        if (!token.num || !canVote(token)) continue;
        const { a, b } = token.num;
        if (a > 12 && b <= 12) day = true;
        else if (b > 12 && a <= 12) month = true;
    }
    if (day && month) return 'conflicting';
    if (day) return 'day-first';
    if (month) return 'month-first';
    return 'none';
}

/** The token's single admissible reading, or null when the document cannot settle the ordering. */
function resolve(token: DateToken, witness: Witness): string | null {
    if (token.readings.length === 1) return token.readings[0];
    if (witness === 'day-first') return token.dayFirst ?? null;
    if (witness === 'month-first') return token.monthFirst ?? null;
    return null;
}

// ─── 4. The gate ─────────────────────────────────────────────────────────────

/**
 * G1. The Act the certificate is issued under, printed on every one of them because the Act says
 * so. Matched against the COLLAPSED sheet, which is what tolerates the line break SIC draws the
 * title across ('MOTOR VEHICLES (THIRD PARTY INSURANCE)' / 'ACT, 1958 (GHANA)'), and stopping at
 * 'act' so the '1958' and '(GHANA)' tails may be written however an insurer likes.
 */
const ACT_TITLE = /motor\s+vehicles?\s*\(?\s*third\s+party\s+insurance\s*\)?\s+act/i;

/*
 * G3. THE COMPETING VOCABULARY, DOCUMENT-WIDE AND WHOLE-WORD.
 *
 * Six adversarial passes each beat a POSITIONAL test — a qualifier on the label's own run, then one
 * on the run above it, then a heading, then a backwards scan, then a narrowed "does this word NAME
 * an instrument" test. Each was beaten the same way: by moving the word. A stamp between the
 * heading and the field, a qualifier on the far side of the value, a previous-policy block printed
 * BEFORE the labels instead of above them, a word bound to its noun in prose position rather than
 * caption position. Position is not a defence. Presence is.
 *
 * So: any of these words anywhere in the text means the document is talking about some OTHER
 * instrument or some OTHER period — a renewal notice, a cover note, a quotation, a road-tax
 * sticker, a brown card, an endorsement, an addendum, a fleet schedule, or the policy this one
 * replaces — and this parser will not read a period off it at all.
 *
 * THE LIST IS CUT AGAINST THE SIC TEMPLATE'S OWN PROSE, which is the constraint that makes it
 * usable rather than a switch that turns the feature off:
 *
 *  - 'permitted', 'permission' and 'licensing' are printed in the statutory wording of fields 5 and
 *    7 on every certificate. 'permit', 'licence no' and 'driving licence' are whole-word anchored,
 *    so none of the three matches ('\bpermit\b' cannot match 'permitted').
 *  - 'hire-purchase' is in field 5's wording; 'purchase' is not on the list.
 *  - BARE 'renewal' IS DELIBERATELY ABSENT. SIC stamps 'Certificate Type: Renewal' on every renewed
 *    policy and some insurers title the document 'RENEWAL CERTIFICATE'; a renewal certificate is a
 *    certificate, and striking it would turn the feature off for a large share of the real corpus.
 *    What IS on the list is the vocabulary a notice cannot avoid while bare 'renewal' stays free —
 *    the noun phrases an insurer names the document with ('renewal notice', 'notice of renewal',
 *    'renewal advice', 'renewal invoice', 'renewal statement', 'renewal form', 'renewal
 *    particulars', 'continuation notice', 'debit note'), the verbs it chases with ('due for
 *    renewal', 'we invite you to renew', 'renewing', 'renewed', 'being renewed'), the threat it
 *    makes ('lapse', 'grace period'), and the words it asks for money in ('premium now due',
 *    'reminder', 'expires shortly', 'ends soon'). A renewal NOTICE reprinting fields 3 and 4 is the
 *    single most dangerous document this parser can be handed: the period on it is one clean year
 *    from the right answer. What no list closes is the notice that prints none of these words —
 *    see STATED RESIDUALS in the module docblock, and the ended-cover sentence that answers it.
 *  - 'specimen', 'sample certificate' and 'claim form' are not renewal words at all: they are the
 *    blank template an insurer emails on request, and the accident paperwork that carries its own
 *    'date of accident'. Neither is a live instrument, and both print statutory-looking fields.
 *    'date of accident' is ALSO a noise label below; here it refuses the document outright, which
 *    is the right answer for a claim form.
 *  - 'prev' with no trailing dot covers 'Prev.' — the '.' is a word boundary.
 */
const COMPETING_VOCABULARY_SOURCE = [
    // the policy this one replaces
    'previous',
    'prev',
    'prior',
    'old\\s+(?:policy|cover)',
    'last\\s+(?:policy|cover)',
    'expiring',
    'expired',
    'former',
    'cancell\\w*',
    'supersed\\w*',
    'replac\\w*',
    'outgoing',
    'ceased',
    'terminated',
    'void',
    'earlier',
    'preceding',
    'histor\\w*',
    'past\\s+(?:cover|policy)',
    // a renewal NOTICE, which is not a certificate
    'renewal\\s+(?:notice|invitation|reminder)',
    'notice\\s+of\\s+renewal',
    'renewal\\s+(?:advice|invoice|statement|application|form|particulars|period|due)',
    'due\\s+for\\s+renewal',
    'invite\\w*\\s+(?:you\\s+)?to\\s+renew',
    'being\\s+renewed',
    'renewed',
    'renewing',
    // Subsumes the 'lapsed' that used to sit in the previous-policy group above: a notice threatens
    // a lapse ('your cover will lapse on…') where a certificate never mentions one.
    'lapse\\w*',
    'grace\\s+period',
    'continuation\\s+notice',
    'debit\\s+note',
    'premium\\s+(?:now\\s+)?due',
    'remind\\w*',
    'expires\\s+shortly',
    'ends\\s+soon',
    // not a live instrument at all: a blank, a sample, or a claim
    'specimen',
    'sample\\s+certificate',
    'claim\\s+form',
    'date\\s+of\\s+accident',
    // another instrument entirely
    'quotation',
    'quote',
    'proposal',
    'cover\\s+note',
    'brown\\s+card',
    'carte\\s+brune',
    'ecowas',
    'cedeao',
    'road\\s*worth\\w*',
    'road\\s+tax',
    'sticker',
    'driving\\s+licen[cs]e',
    'licen[cs]e\\s+no',
    'permit',
    // a second period bolted onto the same policy, or somebody else's policy
    'endorsement',
    'addendum',
    'amendment',
    'extension',
    'rider',
    'buy-?\\s*back',
    'fleet\\s+policy',
].join('|');
const COMPETING_VOCABULARY = new RegExp(`\\b(?:${COMPETING_VOCABULARY_SOURCE})\\b`, 'i');

/*
 * G2. THE TWO PHRASES THE ACT FIXES, and the only two this parser anchors on. Field 3 reads
 * 'EFFECTIVE Date of the Commencement of Insurance for the Purpose of the Act' and field 4 'Date of
 * Expiry of Insurance'; both are matched on their stable head, because Ghana prints 'for the Purpose
 * of the Act' where modern UK prints 'for the purposes of the relevant law'.
 *
 * EVERY GENERIC LABEL IS GONE. 'Cover Starts', 'Cover Expires', 'Period of Insurance', 'Valid
 * From/To', 'Date of Commencement of Insurance' (an insurer's paraphrase, NOT field 3), 'Start
 * Date', 'Expiry Date', 'Effective Date', 'End Date', 'Expires on' — all of them anchored real
 * cover periods and all of them anchored somebody else's, and the second kind cannot be told from
 * the first by anything printed on the page. They come back only on the evidence described in the
 * docblock.
 */
const STATUTORY_START = /effective\s+date\s+of\s+(?:the\s+)?commencement\s+of\s+insurance/gi;
const STATUTORY_END = /date\s+of\s+expiry\s+of\s+insurance/gi;

/*
 * NOISE. These do not merely fail to anchor — they POISON: a token a noise label can reach is a
 * token the statutory labels may not use, because the two readings of that token mean different
 * things and nothing on the page says which won.
 *
 * The caption rule already terminates a window at ANY caption-shaped run, so this list is not what
 * stops an unknown caption from being read — it is what POISONS a value the unknown caption and a
 * statutory label can both reach, which happens whenever the two share a run or a value block.
 *
 * SEVERAL ENTRIES ARE NOW SUBSUMED BY G3 (a document printing 'Sticker Expiry' or 'Cancellation
 * Date' never gets this far). They are kept because they cost nothing and because they are the
 * defence that still stands if G3 is ever narrowed on real-sample evidence.
 *
 * DELIBERATE COLLISION: the SIC certificate's 'Issued Date & Time:' is a print stamp, while the NIC
 * MID portal binds its 'Issued Date' column to the policy's start date. Classified as noise — right
 * for the statutory certificate this parser now reads, and the MID printout is outside the gate
 * anyway.
 */
const NOISE_LABELS = [
    /issu(?:ed?|ing|ance)\s+date/gi,
    /date\s+(?:of\s+)?issu(?:ed?|ing|ance)/gi,
    /issued\s+on/gi,
    /print(?:ed|ing)?\s+(?:on|date)/gi,
    /date\s+print(?:ed)?/gi,
    /date\s+of\s+birth/gi,
    /\bd\.?o\.?b\b/gi,
    /date\s+of\s+purchase/gi,
    /date\s+of\s+accident/gi,
    /payment\s+date/gi,
    /date\s+of\s+payment/gi,
    /receipt\s+date/gi,
    /date\s+of\s+receipt/gi,
    /proposal\s+date/gi,
    /date\s+of\s+proposal/gi,
    /transaction\s+date/gi,
    /date\s+created/gi,
    /date\s+used/gi,
    /date\s+reported/gi,
    /date\s+of\s+(?:first\s+)?registration/gi,
    /proposer'?s?\s+signature/gi,
    /previous(?:ly)?\s+insur/gi,
    /prior\s+insur/gi,
    /renewal\s+date/gi,
    /sticker\s+expir\w*/gi,
    /endorsement\s+(?:effective\s+)?date/gi,
    /date\s+of\s+endorsement/gi,
    /effective\s+date\s+of\s+endorsement/gi,
    /cancellation\s+(?:date|effective)/gi,
];

type LabelKind = 'start' | 'end' | 'noise';

interface LabelMatch {
    start: number;
    end: number;
    text: string;
    kind: LabelKind;
}

function findLabels(s: string): LabelMatch[] {
    const found: LabelMatch[] = [];
    const scan = (patterns: RegExp[], kind: LabelKind) => {
        for (const re of patterns) {
            re.lastIndex = 0;
            let m: RegExpExecArray | null;
            while ((m = re.exec(s)) !== null) {
                found.push({ start: m.index, end: m.index + m[0].length, text: m[0], kind });
                if (m[0].length === 0) re.lastIndex += 1;
            }
        }
    };
    scan([STATUTORY_START], 'start');
    scan([STATUTORY_END], 'end');
    scan(NOISE_LABELS, 'noise');
    // Document order, longest first where two labels start together, so the statutory phrase is
    // the one reported back in `labels`.
    return found.sort((a, b) => a.start - b.start || b.end - a.end);
}

// ─── 5. Claims ───────────────────────────────────────────────────────────────

type Role = 'start' | 'end';

interface Claim {
    role: Role;
    token: DateToken;
    label: LabelMatch;
}

interface Reach {
    inWindow: DateToken[];
    /** The window stopped short of its character cap — a label or a caption cut it off. */
    truncated: boolean;
    windowEnd: number;
}

/**
 * A label reaches forward only, never back — no observed Ghanaian or UK layout prints a date BEFORE
 * its label — and it stops dead at whichever comes first: its character cap, the next label of any
 * kind, or the start of the next CAPTION-SHAPED run. The caption terminator is the one that needs
 * no vocabulary, and it is what makes '3. Effective Date of the Commencement of Insurance ...:' /
 * 'Date of Issuance: 10/04/2026' refuse instead of prefilling the issue date.
 *
 * A TABLE HEADER NEEDS NO SPECIAL RULE ANY MORE. When both statutory phrases are printed side by
 * side over a value row, field 3's window is truncated by field 4's label and reaches nothing,
 * while field 4 reaches a value row of TWO dates — which `claimTokens` refuses as `contested`,
 * because which column a label owns is not a thing this parser may guess. The explicit header-row
 * cap existed for the generic labels ('Start Date Expiry Date' over a premium schedule) and went
 * with them.
 */
function reachOf(
    label: LabelMatch,
    all: LabelMatch[],
    captionStarts: number[],
    tokens: DateToken[],
): Reach {
    const cap = label.end + WINDOW_CHARS;
    // Every label truncates every other label's window: a noise label sitting between a statutory
    // label and a value must still keep that value out of reach.
    const nextLabel = all.find(other => other.start > label.end);
    const nextCaption = captionStarts.find(c => c > label.end) ?? Infinity;
    const boundary = Math.min(nextLabel ? nextLabel.start : Infinity, nextCaption);
    const windowEnd = Math.min(cap, boundary);
    return {
        inWindow: tokens.filter(t => t.start >= label.end && t.start < windowEnd),
        truncated: boundary < cap,
        windowEnd,
    };
}

/**
 * The offsets of every token a noise label can reach. When a noise label's window is truncated to
 * nothing — the label-block/value-block shape, where the next caption follows immediately and the
 * values are all printed afterwards — it poisons the next token in document order instead, so the
 * first value in the block is never read as a cover date.
 *
 * Computed ONCE, over every token in the document and keyed by start offset: a noise label whose
 * own value is decades out of range has still found its value, and it must not go reaching forward
 * for somebody else's.
 */
function noiseClaims(all: LabelMatch[], captionStarts: number[], tokens: DateToken[]): Set<number> {
    const poisoned = new Set<number>();
    for (const label of all) {
        if (label.kind !== 'noise') continue;
        const { inWindow, truncated, windowEnd } = reachOf(label, all, captionStarts, tokens);
        if (inWindow.length > 0) {
            for (const token of inWindow) poisoned.add(token.start);
            continue;
        }
        if (!truncated) continue;
        const next = tokens.find(t => t.start >= windowEnd);
        if (next) poisoned.add(next.start);
    }
    return poisoned;
}

/**
 * The roles a label CLAIMED AND COULD NOT USE, which no later label may fill.
 *
 * A caption-shaped value run that BEGINS with a date is a real value this parser refuses on purpose
 * ('18/04/2026 (see note)'), and the refusal must not leak. With only the two statutory phrases
 * left, the label that could fill the role behind a refusal is the SAME FIELD reprinted on a later
 * page, so the shape this closes is a two-page certificate whose first page's field 3 is annotated
 * and whose second page's is not: the parser refuses rather than quietly answering from the page
 * that happens to be cleaner.
 */
function closedRoles(
    consulted: LabelMatch[],
    all: LabelMatch[],
    runs: Run[],
    captionStarts: number[],
    tokens: DateToken[],
): Partial<Record<Role, number>> {
    const closed: Partial<Record<Role, number>> = {};
    for (const label of consulted) {
        const { inWindow } = reachOf(label, all, captionStarts, tokens);
        if (inWindow.length > 0) continue;
        const cap = label.end + WINDOW_CHARS;
        const next = runs.find(r => r.start >= label.end && r.start < cap);
        if (!next || !next.caption) continue;
        if (!tokens.some(t => t.start === next.start)) continue;
        const role: Role = label.kind === 'start' ? 'start' : 'end';
        if (closed[role] === undefined) closed[role] = label.start;
    }
    return closed;
}

interface ClaimSet {
    claims: Claim[];
    contested: boolean;
}

/**
 * Which token each statutory label answers with, over the WHOLE token list — the year window is
 * applied to the claims afterwards, not to the tokens a label may see, so an implausible date
 * printed where the value belongs stays that label's answer instead of stepping aside for the next
 * token in the document ('4. Date of Expiry of Insurance: 18/04/2062 18/04/2026' anchors nothing,
 * rather than quietly reading the 2026).
 */
function claimTokens(
    all: LabelMatch[],
    consulted: LabelMatch[],
    runs: Run[],
    captionStarts: number[],
    tokens: DateToken[],
    poisoned: Set<number>,
    inYearWindow: (token: DateToken) => boolean,
): ClaimSet {
    const claims: Claim[] = [];
    const coverTokens = new Set<DateToken>();
    let contested = false;

    for (const label of consulted) {
        const { inWindow } = reachOf(label, all, captionStarts, tokens);
        if (inWindow.length === 0) continue;
        /*
         * A SINGLE-DATE LABEL ANSWERED BY A ROW OF TWO DATES IS A COLUMN IT CANNOT PICK. '3.
         * Effective Date of the Commencement of Insurance ...:' / '10/04/2026 18/04/2026' handed
         * back the FIRST date on the row on no better grounds than reading order, and the first
         * column of a value row is as often the issue stamp as the inception. The same refusal
         * covers a header row of both statutory phrases over one value row.
         */
        const valueRun = runIndexAt(runs, inWindow[0].start);
        if (inWindow.filter(t => runIndexAt(runs, t.start) === valueRun).length >= 2) {
            contested = true;
        }
        const role: Role = label.kind === 'start' ? 'start' : 'end';
        claims.push({ role, token: inWindow[0], label });
        coverTokens.add(inWindow[0]);
    }

    for (const token of coverTokens) {
        if (poisoned.has(token.start) && inYearWindow(token)) contested = true;
    }
    // A claimed token the year window rejects leaves its role unanchored; the label has answered,
    // and it does not get to answer twice.
    return { claims: claims.filter(claim => inYearWindow(claim.token)), contested };
}

// ─── 6. The vehicle the certificate names ────────────────────────────────────

/*
 * THE SAME MACHINERY, POINTED AT TWO IDENTIFIERS INSTEAD OF TWO DATES, AND BEHIND THE SAME GATE.
 * A registration number and a chassis number are read out of the sheet exactly the way a date is:
 * a label reaches FORWARD only, its window ends at the first CAPTION-SHAPED run or at the next
 * label of any kind, and only a run whose whole text is the shape of the identifier answers.
 *
 * THIS IS COMPUTED ONLY ON A `found` READ, WHICH IS THE COUPLING AND IT IS DELIBERATE. G1 and G3
 * are what make an identifier trustworthy, not just a date: the one cover STICKER in the
 * production corpus prints a correct plate under the generic label `Reg No`, and admitting that
 * label class here would mean reading identity off exactly the documents whose dates the six
 * adversarial passes ruled out. On the measured corpus the coupling costs nothing — all 39
 * gate-passing certificates print both identifiers and all 39 read `found`.
 *
 * THE LABELS ARE THE STATUTORY ONES AND NOTHING ELSE. Field 1 is printed verbatim as `1. Index
 * Mark and Registration Number of Vehicle` on 39 of 39 — the Ghanaian form prefixes the bare
 * statutory phrase with 'Index Mark and', so the prefix is optional rather than assumed. The
 * chassis sits below field 8's footnote under `Chassis Number:` on 39 of 39; there is no 'VIN'
 * label anywhere in the corpus, and `\bvin\b` is accepted as a synonym only because some insurers
 * print it — anchored on word boundaries so it cannot match the 'vin' inside 'driving', which is
 * where all 117 of the corpus's case-insensitive 'vin' hits actually live.
 *
 * A LABEL TRUNCATES *AND* SPENDS, BECAUSE AN IDENTIFIER HAS NO SECOND READING. The date parser
 * poisons a token two labels can both reach, since '10/04/2026' means different things to an issue
 * stamp and to field 3. Here the danger is simpler: a blank chassis field followed by `Engine No` /
 * `ABC12-34567` would hand an engine number to the chassis check. Truncation alone closes that in
 * ONE direction only — it protects the labels that come AFTER the noise — so the labels CLAIM IN
 * DOCUMENT ORDER and every claimed run is SPENT, noise labels included, which closes the other.
 *
 * AND WHERE THE LABELS ARRIVE IN A BLOCK, THE VALUES ARE ALIGNED BY INDEX RATHER THAN INTERLEAVED.
 * `Engine No` / `Chassis Number` / `2NZ1234567` / `SCP90-5153010` is the two-column detail row every
 * insurer draws, and it is the shape pdfjs emits for any table: the header cells first, in one row,
 * then the value cells, in the next. NO label's value is beside it there, and the k-th value belongs
 * to the k-th label. So labels that truncated each other WITH NO IDENTIFIER-SHAPED RUN BETWEEN THEM
 * are one BLOCK, and the k-th label in a block claims the k-th identifier-shaped run after the
 * block — bounded, like every window here, by the character cap and by the first caption-shaped run.
 * Labels that are NOT in a block keep the interleaved rule: the first unspent shaped run in reach.
 *
 * AN INTERLEAVED WINDOW READS THAT ROW OFF BY ONE, WHICH IS THE DEFECT THIS REPLACED. On
 * `1. Index Mark and Registration Number of Vehicle` / `Engine Number` / `Chassis Number` /
 * `AS 7233-22` / `2NZ1234567` / `SCP90-5145554`, a statutory label that claims nothing when a later
 * label truncates its window leaves the PLATE unspent; the engine label reaching past the block took
 * the plate, and the chassis label took the engine number — a wrong identifier, and so A REFUSED
 * CERTIFICATE FOR THE CORRECT CAR. With a second noise caption in the row it shifted by two.
 * Reaching further could never fix it and spending in order could not either: only the column the
 * label sits in says which value is its own.
 *
 * FEWER VALUES THAN LABELS MEANS NOBODY IN THE BLOCK READS, WHICH IS WHERE THE SAFETY LIVES.
 * `Chassis Number` / `Engine No` / `2NZ-1234567` is a BLANK chassis field above a printed engine
 * number: two labels, one value, and nothing on the page says which column the value is. Alignment
 * is unknowable, so every label in that block reads NOTHING — the same answer a blank statutory
 * field 1 truncated by the caption beside it gets, and exactly as `claimTokens` leaves a role
 * unanchored rather than letting its label answer twice. An identifier nobody could place refuses
 * nothing, which is the safe direction and the only one available.
 *
 * TWO COSTS ARE ACCEPTED RATHER THAN HIDDEN. A certificate whose field 1 is printed BLANK, with no
 * second label beside it to make a block and an exactly plate-shaped bare run below, reads that run
 * as the plate: the interleaved rule applies and `PLATE_SHAPE` is the only guard — narrow enough
 * that a make or a policyholder's name is not one. And a certificate naming TWO vehicles reads the
 * first block, because each identifier is kept only until it is set and this module answers with one
 * vehicle.
 *
 * NOTHING HERE CAN FAIL A DATE READ. The scan runs after the period has resolved, touches none of
 * the date machinery's state, and every end of it is an absent key.
 */

/** Statutory field 1, with the Ghanaian 'Index Mark and' prefix tolerated rather than assumed. */
const VEHICLE_REGISTRATION_LABELS = [
    /(?:index\s+mark\s+and\s+)?registration\s+number\s+of\s+vehicle/gi,
];

/** `Chassis Number:` on all 39; `VIN` accepted as a synonym, never the 'vin' inside 'driving'. */
const VEHICLE_CHASSIS_LABELS = [/chassis\s+(?:number|no)\b\.?/gi, /\bvin\b/gi];

/**
 * Identifiers that are not this vehicle's: they anchor nothing, they end every other label's window,
 * and they SPEND the value they answer with.
 *
 * THE LAST FIVE ARE NOT VEHICLE FIELDS AT ALL, AND THAT IS WHY THEY ARE HERE. `CHASSIS_SHAPE` is a
 * generic identifier shape (see below), so what a certificate prints BESIDE the chassis matters as
 * much as what looks like it: `Policy Number` / `Chassis Number` / `MV1234567` / `SCP90-5153010`
 * read the policy number as the frame number and refused the document. Naming them makes them both
 * truncate and spend, which is the whole defence — the shape cannot be tightened into one, because
 * an imported car's 17-character VIN and a Ghanaian `SCP90-5153010` share no narrower pattern.
 */
const VEHICLE_NOISE_LABELS = [
    /engine\s+(?:number|no)\b\.?/gi,
    /\btrailer\b/gi,
    /previous\s+registration/gi,
    /\bregistration\s+number\s+of\s+trailer/gi,
    /policy\s+(?:number|no)\b\.?/gi,
    /certificate\s+(?:number|no)\b\.?/gi,
    /reference\s+(?:number|no)\b\.?/gi,
    /cover\s+note/gi,
    /sticker\s+(?:number|no)\b\.?/gi,
];

/**
 * Forward reach of a vehicle label in characters, and — measured from the LAST label in a block,
 * whose reach is the longest of the block's — the cap on the value row a block may align against.
 * Shorter than `WINDOW_CHARS` because the statutory layout prints the value on the very next run —
 * label, blank run (which `buildSheet` drops), value.
 *
 * IT IS NOT WHAT KEEPS THE NEXT FIELD OUT, AND TUNING IT NEVER COULD BE. 80 characters already
 * spans a two-column detail row several times over: in `Engine Number` / `Chassis Number` /
 * `Colour` / `2NZ1234567` / `SCP90-5153010` the WRONG value sits seven characters past the chassis
 * label, so no number both reaches the real value on the live template and refuses the neighbour's.
 * What protects the read is the block-alignment rule above — in a row of labels the k-th value is
 * the k-th label's, and every claimed run is spent — so the neighbour's value is neither in the
 * chassis label's column nor still available by the time it looks.
 */
const VEHICLE_WINDOW_CHARS = 80;

/**
 * THE GHANAIAN PLATE, AND ONLY IT. All 39 printed plates fit `AA 9999-99` or `AA 999-99`; this
 * tolerates one to three index letters and either separator, or none.
 *
 * THE GENERIC FALLBACK WAS CONSIDERED AND REFUSED. `^[A-Z0-9][A-Z0-9\s-]{3,12}$` matches ordinary
 * words — `TOYOTA`, `GHANA`, `VITZ` — so on a certificate whose field 1 is printed blank the scan
 * would walk past the unlabelled `2. Name of Policy Holder` run and hand back a make or a name as
 * the plate. That manufactures a mismatch, and a mismatch is now a REFUSAL rather than a remark.
 * A plate this parser cannot recognise reads `absent`, which refuses nothing — the safe direction.
 */
const PLATE_SHAPE = /^[A-Z]{1,3}[\s-]?\d{1,5}[\s-]?\d{2}$/i;

/**
 * A frame number or a VIN: a series, an optional separator, a serial. The corpus is 13–14 characters
 * and never 17 (`SCP90-5153010`, `NSP1302110905`), but the upper bound admits a 17-character ISO
 * VIN, which is what an imported car's certificate would print.
 *
 * THE SEPARATOR IS `[\s.-]`, WHICH IS EXACTLY THE SET `normaliseIdentifier` STRIPS. A template that
 * draws `SCP90 5153010` or `SCP90.5153010` has printed the frame number one that draws
 * `SCP90-5153010` printed, and the comparison folds all three together — so admitting only the dash
 * left this shape as the one thing quietly declining to look at those documents at all.
 *
 * AND IT MUST CARRY A DIGIT, WHICH IS WHAT THE SPACE COST. With `[\s.-]` the pattern alone accepts
 * `TOYOTA COROLLA`; every frame number and every ISO VIN carries digits and a make and model does
 * not. This shape is the LOOSE one either way, which is why its neighbours are named above rather
 * than guarded here.
 */
const CHASSIS_SHAPE = /^[A-Z0-9]{2,8}[\s.-]?[A-Z0-9]{5,12}$/i;
const CHASSIS_DIGIT = /\d/;

type VehicleLabelKind = 'registration' | 'chassis' | 'noise';

interface VehicleLabel {
    start: number;
    end: number;
    kind: VehicleLabelKind;
}

function findVehicleLabels(s: string): VehicleLabel[] {
    const found: VehicleLabel[] = [];
    const scan = (patterns: RegExp[], kind: VehicleLabelKind) => {
        for (const re of patterns) {
            re.lastIndex = 0;
            let m: RegExpExecArray | null;
            while ((m = re.exec(s)) !== null) {
                found.push({ start: m.index, end: m.index + m[0].length, kind });
                if (m[0].length === 0) re.lastIndex += 1;
            }
        }
    };
    scan(VEHICLE_REGISTRATION_LABELS, 'registration');
    scan(VEHICLE_CHASSIS_LABELS, 'chassis');
    scan(VEHICLE_NOISE_LABELS, 'noise');
    // Document order, longest first where two start together, so the statutory phrase wins over a
    // synonym matched inside it.
    const ordered = found.sort((a, b) => a.start - b.start || b.end - a.end);
    /*
     * ONE PHRASE IS ONE LABEL, WHICH MATTERS NOW THAT LABELS SPEND. `\btrailer\b` matches inside
     * `Registration Number of Trailer`, and two labels over one caption would truncate each other's
     * windows to nothing and then spend TWO values where the document printed one — taking the real
     * plate out of statutory field 1's reach. The longer match, which sorts first, is the one kept.
     */
    const labels: VehicleLabel[] = [];
    for (const label of ordered) {
        const last = labels[labels.length - 1];
        if (last !== undefined && label.start < last.end) continue;
        labels.push(label);
    }
    return labels;
}

/** A run's text as a candidate identifier: trimmed, and without the punctuation a template trails. */
function candidateOf(run: Run): string {
    return run.text.trim().replace(/[.,]+$/, '');
}

/** The chassis shape and the digit it must carry, which are one test and always asked together. */
function isChassis(text: string): boolean {
    return CHASSIS_SHAPE.test(text) && CHASSIS_DIGIT.test(text);
}

/** Either identifier's shape — what a label, statutory or noise, may claim and so spend. */
function isIdentifier(text: string): boolean {
    return PLATE_SHAPE.test(text) || isChassis(text);
}

interface VehicleReach {
    /** The identifier-shaped runs inside the window, in document order. */
    shaped: Run[];
    /** The window was cut short by ANOTHER LABEL rather than by a caption or the character cap. */
    blocked: boolean;
}

/**
 * What one vehicle label can see: forward only, ending at its character cap, at the next label of
 * any kind, or at the first CAPTION-SHAPED run — the same three terminators `reachOf` applies to
 * dates.
 *
 * A RUN THE LABEL'S OWN MATCH OVERLAPS IS NOT IN REACH, which is what refuses the inline form
 * (`Chassis Number: SCP90-5153010` drawn as one run). That run is caption-shaped by the same test
 * that judges a date's value run, and a caption ends the window rather than answering it — the rule
 * the date parser needed no vocabulary for, kept here for the same reason.
 *
 * `blocked` IS THE PRECONDITION FOR JOINING A BLOCK, and it asks which terminator came first. A
 * caption in between means the runs after it are some other field's, not the tail of a row of
 * labels, so a window a caption closed is never continued into the next label's.
 */
function vehicleReach(label: VehicleLabel, labels: VehicleLabel[], runs: Run[]): VehicleReach {
    const cap = label.end + VEHICLE_WINDOW_CHARS;
    const nextLabel = labels.find(other => other.start > label.end);
    const nextLabelStart = nextLabel ? nextLabel.start : Infinity;
    const nextCaption = runs.find(run => run.start >= label.end && run.caption)?.start ?? Infinity;
    const windowEnd = Math.min(cap, nextLabelStart, nextCaption);
    const shaped = runs.filter(
        run => run.start >= label.end && run.start < windowEnd && isIdentifier(candidateOf(run)),
    );
    return { shaped, blocked: nextLabelStart <= nextCaption && nextLabelStart < cap };
}

/**
 * The labels cut into BLOCKS: a label joins the next one when it was truncated BY that label and no
 * identifier-shaped run was printed in between — which is exactly the label-row/value-row shape,
 * and exactly the case where a label's own value cannot be beside it. Every other label stands
 * alone and keeps the interleaved rule.
 *
 * THE TEST IS STRUCTURAL, NOT A LEFTOVER OF WHAT WAS ALREADY CLAIMED. A label whose only shaped run
 * an earlier label already spent is still NOT in a block: its value was printed beside it, it was
 * simply somebody else's, and joining the next label's column on those grounds would align a row
 * that was never drawn.
 */
function vehicleBlocks(labels: VehicleLabel[], runs: Run[]): VehicleLabel[][] {
    const blocks: VehicleLabel[][] = [];
    let block: VehicleLabel[] = [];
    for (const label of labels) {
        block.push(label);
        const { shaped, blocked } = vehicleReach(label, labels, runs);
        if (blocked && shaped.length === 0) continue;
        blocks.push(block);
        block = [];
    }
    // `blocked` is false for the last label — nothing can truncate it — so this only ever guards.
    if (block.length > 0) blocks.push(block);
    return blocks;
}

/**
 * The value row a block aligns against: the unspent identifier-shaped runs after the block's last
 * label, in document order, ending at that label's character cap or at the first caption-shaped run.
 */
function vehicleBlockValues(block: VehicleLabel[], runs: Run[], spent: Set<number>): Run[] {
    const from = block[block.length - 1].end;
    const nextCaption = runs.find(run => run.start >= from && run.caption)?.start ?? Infinity;
    const windowEnd = Math.min(from + VEHICLE_WINDOW_CHARS, nextCaption);
    return runs.filter(
        run =>
            run.start >= from &&
            run.start < windowEnd &&
            !spent.has(run.start) &&
            isIdentifier(candidateOf(run)),
    );
}

/** The identifiers this certificate prints, or undefined when it prints neither. */
function readVehicle(s: string, runs: Run[]): CertificateVehicle | undefined {
    const labels = findVehicleLabels(s);
    /** The run starts already claimed: a printed value answers ONE label, the one whose column it is. */
    const spent = new Set<number>();
    const vehicle: CertificateVehicle = {};

    /*
     * THE CLAIM IS SPENT WHICHEVER SHAPE IT TURNED OUT TO BE. A statutory label answered by the
     * OTHER identifier's shape has answered — it reads absent rather than stepping over the run in
     * front of it to find something that fits, which is `claimTokens`'s rule for a label whose token
     * the year window rejected: the label does not get to answer twice.
     */
    const claim = (label: VehicleLabel, run: Run | undefined) => {
        if (run === undefined) return;
        spent.add(run.start);
        if (label.kind === 'noise') return;
        const value = candidateOf(run);
        if (label.kind === 'registration') {
            if (vehicle.registration === undefined && PLATE_SHAPE.test(value)) {
                vehicle.registration = value;
            }
        } else if (vehicle.chassis === undefined && isChassis(value)) {
            vehicle.chassis = value;
        }
    };

    for (const block of vehicleBlocks(labels, runs)) {
        if (block.length === 1) {
            const { shaped } = vehicleReach(block[0], labels, runs);
            claim(block[0], shaped.find(run => !spent.has(run.start)));
            continue;
        }
        const values = vehicleBlockValues(block, runs, spent);
        // Fewer values than labels: which column each value came from is unknowable, so no label in
        // the block reads and no run is spent. See §6 — this is the blank-field case.
        if (values.length < block.length) continue;
        block.forEach((label, k) => claim(label, values[k]));
    }

    return vehicle.registration === undefined && vehicle.chassis === undefined ? undefined : vehicle;
}

// ─── 7. Resolution ───────────────────────────────────────────────────────────

const none = (reason: CoverPeriodReason): CoverPeriodRead => ({ status: 'none', reason });
const ambiguous = (reason: CoverPeriodReason): CoverPeriodRead => ({ status: 'ambiguous', reason });

/**
 * The cover period a statutory certificate's text layer states outright, or why it does not.
 *
 * `now` is used only for the year-plausibility window, and every test passes it explicitly: a
 * fixture written against a hard-coded year would otherwise start failing decades from now.
 */
export function readCoverPeriod(text: string, now: Date = new Date()): CoverPeriodRead {
    const { s, runs } = buildSheet(text ?? '');
    if (!s) return none('no-text');

    // G1 and G3: is this the one document class this parser reads, and is it the only instrument
    // talking on the page? Both are document-wide, and both are cheap enough to run first.
    if (!ACT_TITLE.test(s)) return none('not-a-statutory-certificate');
    if (COMPETING_VOCABULARY.test(s)) return none('not-a-statutory-certificate');

    const labels = findLabels(s);
    const statutory = labels.filter(l => l.kind !== 'noise');
    // G2, the half that is a gate: a certificate under the Act prints BOTH fields. The other half —
    // repeats — is settled further down by counting distinct RESOLVED VALUES rather than labels, so
    // a page-2 reprint of the whole certificate collapses and a disagreement refuses.
    if (!statutory.some(l => l.kind === 'start') || !statutory.some(l => l.kind === 'end')) {
        return none('not-a-statutory-certificate');
    }

    const all = tokenise(s);
    const captionStarts = runs.filter(r => r.caption).map(r => r.start);
    const poisoned = noiseClaims(labels, captionStarts, all);

    const lo = now.getFullYear() - YEARS_BACK;
    const hi = now.getFullYear() + YEARS_AHEAD;
    // Every reading of a token shares its year, so this is a property of the token, not of a
    // reading: the window drops the whole token or none of it.
    const inYearWindow = (token: DateToken) =>
        token.readings.every(iso => {
            const year = Number(iso.slice(0, 4));
            return year >= lo && year <= hi;
        });

    const { claims: allClaims, contested } = claimTokens(
        labels,
        statutory,
        runs,
        captionStarts,
        all,
        poisoned,
        inYearWindow,
    );
    if (contested) return ambiguous('contested');

    // A role a label claimed and could not use is CLOSED: no later reprint of that field may fill it.
    const closed = closedRoles(statutory, labels, runs, captionStarts, all);
    const claims = allClaims.filter(
        claim =>
            closed[claim.role] === undefined || claim.label.start <= (closed[claim.role] as number),
    );

    /*
     * THE WITNESS IS COMPUTED LAST, FROM THE CLAIMS THAT SURVIVED — a token votes on the document's
     * day/month ordering if and only if the answer is partly made of it. The two further tests are
     * belt and braces rather than filters (a poisoned claim has already returned `contested`, and
     * `claims` is already year-filtered), but they keep the rule readable as one expression.
     */
    const claimed = new Set(claims.map(c => c.token.start));
    const canVote = (token: DateToken) =>
        claimed.has(token.start) && !poisoned.has(token.start) && inYearWindow(token);
    const witness = witnessOf(all, canVote);

    // Only a token a statutory label claimed has to be orderable; an issue stamp nobody reads may
    // stay undecidable forever.
    const resolved = claims.map(claim => ({ claim, iso: resolve(claim.token, witness) }));
    if (resolved.some(r => r.iso === null)) return ambiguous('ordering-unknown');

    const starts = resolved.filter(r => r.claim.role === 'start');
    const ends = resolved.filter(r => r.claim.role === 'end');
    const startDates = new Set(starts.map(r => r.iso));
    const endDates = new Set(ends.map(r => r.iso));
    // G2's repeat half: identical repeats across pages collapse to one value — the multi-page
    // answer and a free consistency check in the same rule.
    if (startDates.size > 1 || endDates.size > 1) return ambiguous('disagreeing-repeats');
    // The labels matched, but not one of them could reach a date it was allowed to use.
    if (startDates.size === 0 && endDates.size === 0) return none('no-dates-in-reach');
    if (startDates.size === 0 || endDates.size === 0) return none('partial');

    const startsOn = starts[0].iso as string;
    const expiresOn = ends[0].iso as string;
    // String compare is exact on 'yyyy-MM-dd'. The parser never emits a backwards period; a human
    // may still type one, and `validatePolicyDraft` must keep accepting it.
    if (expiresOn <= startsOn) return ambiguous('backwards');
    const from = parseBusinessDay(startsOn);
    const to = parseBusinessDay(expiresOn);
    if (!from || !to) return ambiguous('backwards');
    if (differenceInCalendarDays(to, from) > MAX_SPAN_DAYS) return ambiguous('implausible-span');

    // READ LAST AND ONLY HERE, so the identifiers can never come back from a document whose dates
    // were refused, and so nothing about them can change what the dates said. A missing or odd
    // vehicle field is an absent key, never a failed read — see §6.
    const vehicle = readVehicle(s, runs);

    return {
        status: 'found',
        startsOn,
        expiresOn,
        raw: { start: starts[0].claim.token.raw, end: ends[0].claim.token.raw },
        labels: { start: starts[0].claim.label.text, end: ends[0].claim.label.text },
        ...(vehicle ? { vehicle } : {}),
    };
}
